// @ts-nocheck
import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { createAvatar } from "@dicebear/core";
import * as openPeeps from "@dicebear/open-peeps";
import logoImg from "../../assets/images/coronando-logo.svg";
import useGetUserInfo from "../../hooks/useGetUserInfo";
import {
  Coins,
  GameController,
  Headset,
  Horse,
  House,
  Newspaper,
  Joystick,
  List,
  ListChecks,
  PokerChip,
  SignOut,
  SoccerBall,
  Spade,
  Ticket,
  UsersThree,
  X,
} from "@phosphor-icons/react";
import { useAuthStore } from "../../store/useAuth.store";
import { useBalanceStore } from "../../store/useBalance.store";
import { useUserSupport } from "../../store/useUserSupport.store";
import usePagoMovilRegistration from "../../hooks/usePagoMovilRegistration";

const PINK = "#d732a8";
const ORANGE = "#fa5400";

// ─── Nav Item ─────────────────────────────────────────────────────────────────
const NavItem = ({ icon: Icon, label, active = false, onClick, isFeed = false, isMenu = false }) => {
  const [pressed, setPressed] = useState(false);

  if (isFeed) {
    return (
      <button
        type="button"
        onClick={onClick}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        onTouchStart={() => setPressed(true)}
        onTouchEnd={() => setPressed(false)}
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 4,
          padding: "0 4px 10px",
          height: "100%",
          background: "none",
          border: "none",
          cursor: "pointer",
          position: "relative",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -20,
            width: 58,
            height: 58,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${ORANGE}, ${PINK})`,
            boxShadow: pressed
              ? `0 0 0 4px rgba(215,50,168,0.2), 0 0 14px rgba(215,50,168,0.5), 0 2px 10px rgba(0,0,0,0.5)`
              : `0 0 0 3px rgba(215,50,168,0.25), 0 0 22px rgba(215,50,168,0.6), 0 0 44px rgba(250,84,0,0.25), 0 4px 20px rgba(0,0,0,0.55)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transform: pressed ? "scale(0.94)" : "scale(1)",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            animation: "feed-glow 2s ease-in-out infinite alternate",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 3,
              borderRadius: "50%",
              background: "linear-gradient(135deg, rgba(255,255,255,0.25) 0%, transparent 60%)",
              pointerEvents: "none",
            }}
          />
          <Icon size={24} color="#fff" weight="fill" />
        </div>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.02em",
            lineHeight: 1.2,
            background: `linear-gradient(90deg, ${ORANGE}, ${PINK})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {label}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        padding: "8px 4px",
        height: "100%",
        background: active
          ? "linear-gradient(135deg, rgba(250,84,0,0.16), rgba(215,50,168,0.16))"
          : pressed
            ? "rgba(215,50,168,0.1)"
            : "none",
        border: "none",
        borderRadius: 16,
        cursor: "pointer",
        transition: "background 0.2s ease",
        margin: "0 2px",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <Icon
        size={22}
        weight={active ? "fill" : "regular"}
        color={active ? PINK : isMenu ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.5)"}
        style={{
          filter: active ? `drop-shadow(0 0 6px ${PINK})` : "none",
          transition: "all 0.2s ease",
        }}
      />
      <span
        style={{
          fontSize: 10,
          fontWeight: active ? 700 : 500,
          color: active ? PINK : "rgba(255,255,255,0.5)",
          letterSpacing: "0.01em",
          lineHeight: 1.2,
          transition: "color 0.2s ease",
        }}
      >
        {label}
      </span>
    </button>
  );
};

// ─── Sidebar Item ─────────────────────────────────────────────────────────────
const SidebarItem = ({ icon: Icon, label, active, isDanger, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        width: "100%",
        padding: "12px 16px",
        background: active
          ? "linear-gradient(135deg, rgba(250,84,0,0.14), rgba(215,50,168,0.14))"
          : hovered
            ? "rgba(215,50,168,0.08)"
            : "none",
        border: "none",
        borderRadius: 14,
        cursor: "pointer",
        transition: "background 0.18s ease",
        WebkitTapHighlightColor: "transparent",
        marginBottom: 2,
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: active
            ? `linear-gradient(135deg, ${ORANGE}, ${PINK})`
            : isDanger
              ? "rgba(239,68,68,0.12)"
              : "rgba(215,50,168,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          boxShadow: active ? `0 0 10px rgba(215,50,168,0.4)` : "none",
          transition: "all 0.2s ease",
        }}
      >
        <Icon
          size={18}
          weight="fill"
          color={active ? "#fff" : isDanger ? "rgb(239,68,68)" : PINK}
        />
      </span>
      <span
        style={{
          fontSize: 14,
          fontWeight: active ? 700 : 500,
          color: isDanger
            ? "rgb(239,68,68)"
            : active
              ? "#fff"
              : "rgba(255,255,255,0.75)",
          transition: "color 0.18s ease",
        }}
      >
        {label}
      </span>
      {active && (
        <div
          style={{
            marginLeft: "auto",
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${ORANGE}, ${PINK})`,
            boxShadow: `0 0 8px ${PINK}`,
          }}
        />
      )}
    </button>
  );
};

// ─── Mobile Sidebar Portal ────────────────────────────────────────────────────
const MobileSidebar = ({ open, onClose, onNavigate, onDeposit, onSupport, onLogout, currentPath }) => {
  const { t } = useTranslation();
  const { data: userData } = useGetUserInfo();
  const balance = useBalanceStore(state => state.balance);
  const authStatus = useAuthStore(state => state.authStatus);
  const isAuthenticated = authStatus === "authenticated";
  const userName = userData?.alias || userData?.given_name || userData?.email || "";

  const avatarSvg = useMemo(() => {
    const seed = userName || "default";
    const avatar = createAvatar(openPeeps, {
      seed,
      size: 48,
      backgroundColor: ["transparent"],
      skinColor: ["brown", "darkBrown", "light", "tanned"],
    });
    return avatar.toDataUri();
  }, [userName]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const items = [
    { key: "/", label: "Página principal", Icon: House },
    { key: "deposit", label: "Recarga saldo", Icon: Coins, action: "deposit" },
    { key: "/user/referrals", label: "Afiliados", Icon: UsersThree },
    { key: "/gameslist/all", label: "Slots", Icon: PokerChip },
    { key: "/gameslist/all_mini", label: "Mini juegos", Icon: Joystick, path: "/crash-games" },
    { key: "/casino", label: "Casino en vivo", Icon: Spade },
    { key: "/horses", label: "Hipismo", Icon: Horse },
    { key: "/lottery", label: "Lotería", Icon: Ticket },
    { key: "/sports", label: "Deportes", Icon: SoccerBall },
    { key: "/games/pragmatic/vplobby", label: "E-Sports", Icon: GameController },
    { key: "support", label: "Atención al cliente", Icon: Headset, action: "support" },
    { key: "/user/transactions", label: "Mis operaciones", Icon: ListChecks },
    { key: "logout", label: "Cerrar sesión", Icon: SignOut, isDanger: true, action: "logout" },
  ];

  const handleItemClick = (item) => {
    if (item.action === "deposit") {
      onDeposit();
    } else if (item.action === "support") {
      onSupport();
      onClose();
    } else if (item.action === "logout") {
      onLogout();
      onClose();
    } else {
      const path = item.path || item.key;
      onNavigate(path);
    }
  };

  return createPortal(
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.72)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          zIndex: 1099,
          opacity: open ? 1 : 0,
          pointerEvents: open ? "all" : "none",
          transition: "opacity 0.28s ease",
        }}
      />

      <div
        role="navigation"
        aria-label="Menú de navegación"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          width: "min(82vw, 300px)",
          background: "rgba(6, 4, 16, 0.97)",
          backdropFilter: "blur(32px) saturate(180%)",
          WebkitBackdropFilter: "blur(32px) saturate(180%)",
          borderRight: "1.5px solid rgba(215,50,168,0.18)",
          boxShadow: open
            ? "4px 0 40px rgba(215,50,168,0.14), 12px 0 80px rgba(0,0,0,0.8)"
            : "none",
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.34s cubic-bezier(0.34, 1.56, 0.64, 1)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "relative",
            padding: "20px 20px 20px",
            background: "linear-gradient(135deg, rgba(250,84,0,0.18) 0%, rgba(215,50,168,0.14) 100%)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -20,
              left: -20,
              width: 120,
              height: 120,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(250,84,0,0.25), transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: -20,
              right: -20,
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(215,50,168,0.2), transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "1px",
              background: `linear-gradient(90deg, transparent, ${ORANGE} 20%, ${PINK} 50%, ${ORANGE} 80%, transparent)`,
            }}
          />

          {/* Logo row + close */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", marginBottom: isAuthenticated ? 16 : 0 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <img
                src={logoImg}
                alt="Coronando"
                style={{ height: 60, width: "auto", objectFit: "contain", display: "block" }}
              />
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "rgba(215,50,168,0.12)",
                border: "1px solid rgba(215,50,168,0.28)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
                flexShrink: 0,
              }}
            >
              <X size={18} weight="bold" color={PINK} />
            </button>
          </div>

          {/* User card — only when logged in */}
          {isAuthenticated && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 12px",
                borderRadius: 14,
                background: "rgba(215,50,168,0.08)",
                border: "1px solid rgba(215,50,168,0.18)",
                position: "relative",
              }}
            >
              {/* Avatar */}
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  border: `2px solid ${PINK}`,
                  boxShadow: `0 0 10px rgba(215,50,168,0.45)`,
                  overflow: "hidden",
                  flexShrink: 0,
                  background: "rgba(6,4,16,0.7)",
                }}
              >
                <img
                  src={avatarSvg}
                  alt={userName}
                  width={48}
                  height={48}
                  style={{ display: "block", borderRadius: "50%" }}
                />
              </div>

              {/* Info */}
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#fff",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 150,
                  }}
                >
                  {userName || "Usuario"}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    marginTop: 2,
                    background: `linear-gradient(90deg, ${ORANGE}, ${PINK})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  $ {Number(balance).toFixed(2)}
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: "10px 10px 100px", flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "rgba(215,50,168,0.6)",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              padding: "8px 16px 4px",
            }}
          >
            Navegación
          </div>

          {items.map((item) => (
            <SidebarItem
              key={item.key}
              icon={item.Icon}
              label={item.label}
              active={!item.action && (currentPath === (item.path || item.key) || (item.path || item.key) !== "/" && currentPath.startsWith(item.path || item.key))}
              isDanger={item.isDanger}
              onClick={() => handleItemClick(item)}
            />
          ))}
        </div>
      </div>
    </>,
    document.body
  );
};

// ─── Main FloatingMenu ────────────────────────────────────────────────────────
const FloatingMenu = ({ onMenuToggle, shouldCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const authStatus = useAuthStore((state) => state.authStatus);
  const handleShowAuthModal = useAuthStore((state) => state.handleShowAuthModal);
  const handleLogout = useAuthStore((state) => state.logout);
  const handleBankAccountModal = useBalanceStore((state) => state.handleBankAccountModal);
  const handleSupportModal = useUserSupport((state) => state.handleSupportModal);
  const { triggerRegistrationModalIfNeeded } = usePagoMovilRegistration();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeNav, setActiveNav] = useState(null);

  useEffect(() => {
    if (shouldCollapse) {
      setSidebarOpen(false);
    }
  }, [shouldCollapse]);

  useEffect(() => {
    const p = location.pathname;
    if (p.startsWith("/gameslist") || p.startsWith("/games")) setActiveNav("slots");
    else if (p === "/casino" || p.startsWith("/live-casino")) setActiveNav("casino");
    else if (p === "/game-feed") setActiveNav("feed");
    else if (p.startsWith("/lottery")) setActiveNav("lottery");
    else setActiveNav(null);
  }, [location.pathname]);

  const checkAuth = (cb) => {
    if (authStatus !== "authenticated") {
      useAuthStore.getState().handleAuthForm("login");
      handleShowAuthModal();
      return;
    }
    cb();
  };

  const go = (path, key) => {
    checkAuth(() => {
      navigate(path);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setActiveNav(key);
      onMenuToggle(false);
    });
  };

  const handleDeposit = async () => {
    const triggered = await triggerRegistrationModalIfNeeded("deposit", true);
    if (!triggered) handleBankAccountModal();
    setSidebarOpen(false);
  };

  const handleSidebarNavigate = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setSidebarOpen(false);
    onMenuToggle(false);
  };

  return (
    <>
      <MobileSidebar
        open={sidebarOpen}
        onClose={() => {
          setSidebarOpen(false);
          onMenuToggle(false);
        }}
        onNavigate={handleSidebarNavigate}
        onDeposit={handleDeposit}
        onSupport={() => handleSupportModal()}
        onLogout={handleLogout}
        currentPath={location.pathname}
      />

      <div className="hidden max-[799px]:block">
        <div
          style={{
            position: "fixed",
            bottom: 16,
            left: 12,
            right: 12,
            height: 68,
            borderRadius: 24,
            background: "rgba(6, 4, 16, 0.92)",
            backdropFilter: "blur(24px) saturate(180%)",
            WebkitBackdropFilter: "blur(24px) saturate(180%)",
            border: "1.5px solid rgba(215, 50, 168, 0.26)",
            animation: "nav-bar-glow 3s ease-in-out infinite alternate",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            overflow: "visible",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: "8%",
              right: "8%",
              height: "1px",
              background: `linear-gradient(90deg, transparent 0%, ${ORANGE} 30%, ${PINK} 50%, ${ORANGE} 70%, transparent 100%)`,
              backgroundSize: "200% 100%",
              animation: "nav-shimmer 5s ease-in-out infinite",
              borderRadius: "999px",
              opacity: 0.7,
            }}
          />

          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 24,
              background: "radial-gradient(ellipse 80% 40% at 50% 100%, rgba(215,50,168,0.07), transparent)",
              pointerEvents: "none",
            }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              width: "100%",
              height: "100%",
              padding: "0 8px",
              position: "relative",
            }}
          >
            <NavItem
              icon={PokerChip}
              label={t("floatingMenuSlots", "Slots")}
              active={activeNav === "slots"}
              onClick={() => go("/gameslist/all", "slots")}
            />
            <NavItem
              icon={Spade}
              label={t("floatingMenuCasino", "Casino")}
              active={activeNav === "casino"}
              onClick={() => go("/casino", "casino")}
            />
            <NavItem
              icon={Newspaper}
              label={t("floatingMenuFeed", "Feed")}
              active={activeNav === "feed"}
              onClick={() => go("/game-feed", "feed")}
            />
            <NavItem
              icon={Ticket}
              label={t("floatingMenuLottery", "Lotería")}
              active={activeNav === "lottery"}
              onClick={() => go("/lottery", "lottery")}
            />
            <NavItem
              icon={List}
              label="Menú"
              isMenu
              onClick={() => setSidebarOpen(true)}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default FloatingMenu;